import { c as _c } from "react/compiler-runtime";
import { FFLogo } from "@repo/ui/components/brand/FFLogo";
import { Avatar, AvatarFallback } from "@repo/ui/components/ui/avatar";
import { Button } from "@repo/ui/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@repo/ui/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@repo/ui/components/ui/sheet";
import { cn } from "@repo/ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { Camera, Compass, LogIn, LogOut, Map, Menu, Settings, Shield, Trophy } from "lucide-react";
import { useState } from "react";
import { authClient } from "@/clients/authClient";
import { useIsAdmin } from "@/lib/hooks/useIsAdmin";
import { useLoginModal } from "@/lib/hooks/useLoginModal";
import { NAV_LOGIN_BUTTON, NAV_USER_MENU } from "@/lib/utils/testIds";
import { trpc } from "@/router";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const NAV_LINKS = [{
  to: "/upptack",
  label: "Upptäck",
  icon: Compass
}, {
  to: "/karta",
  label: "Karta",
  icon: Map
}, {
  to: "/topplista",
  label: "Topplista",
  icon: Trophy
}];
export function AppNav() {
  const $ = _c(40);
  const {
    data: session
  } = authClient.useSession();
  const user = session?.user;
  const isAdmin = useIsAdmin();
  const loginModal = useLoginModal();
  const navigate = useNavigate();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      select: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const pathname = useRouterState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = trpc.features.getPendingCount.queryOptions();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isAdmin) {
    t2 = {
      ...t1,
      enabled: isAdmin,
      refetchInterval: 30000
    };
    $[2] = isAdmin;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: pendingCount
  } = useQuery(t2);
  let t3;
  if ($[4] !== user?.name) {
    t3 = user?.name?.split(" ").map(_temp2).join("").toUpperCase().slice(0, 2);
    $[4] = user?.name;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const initials = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = /*#__PURE__*/_jsx("span", {
      className: "md:hidden",
      children: /*#__PURE__*/_jsx(FFLogo, {
        variant: "icon"
      })
    });
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = /*#__PURE__*/_jsxs(Link, {
      to: "/",
      className: "flex items-center",
      children: [t4, /*#__PURE__*/_jsx("span", {
        className: "hidden md:block",
        children: /*#__PURE__*/_jsx(FFLogo, {
          variant: "full"
        })
      })]
    });
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== pathname) {
    t6 = NAV_LINKS.map(t7 => {
      const {
        to,
        label,
        icon: Icon
      } = t7;
      const isActive = pathname.startsWith(to);
      return /*#__PURE__*/_jsxs(Link, {
        to: to,
        className: cn("flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors", isActive ? "bg-brand/10 text-brand" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"),
        children: [/*#__PURE__*/_jsx(Icon, {
          className: "h-4 w-4"
        }), label]
      }, to);
    });
    $[8] = pathname;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== isAdmin || $[11] !== pathname || $[12] !== pendingCount) {
    t7 = isAdmin && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("div", {
        className: "mx-1 h-5 w-px bg-gray-200"
      }), /*#__PURE__*/_jsxs(Link, {
        to: "/admin",
        className: cn("relative flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors", pathname.startsWith("/admin") ? "bg-brand/10 text-brand" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"),
        children: [/*#__PURE__*/_jsx(Shield, {
          className: "h-4 w-4"
        }), "Admin", pendingCount != null && Number(pendingCount) > 0 && /*#__PURE__*/_jsx("span", {
          className: "absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white",
          children: Number(pendingCount)
        })]
      })]
    });
    $[10] = isAdmin;
    $[11] = pathname;
    $[12] = pendingCount;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = /*#__PURE__*/_jsxs("div", {
      className: "flex items-center gap-4",
      children: [t5, /*#__PURE__*/_jsxs("nav", {
        className: "hidden items-center gap-1 md:flex",
        children: [t6, t7]
      })]
    });
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== navigate) {
    t9 = () => navigate({
      to: "/bidra"
    });
    $[17] = navigate;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = /*#__PURE__*/_jsx(Camera, {
      className: "h-3.5 w-3.5"
    });
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t9) {
    t11 = /*#__PURE__*/_jsxs(Button, {
      size: "sm",
      className: "hidden gap-1.5 bg-brand hover:bg-brand/90 sm:flex",
      onClick: t9,
      children: [t10, "Bidra"]
    });
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== initials || $[23] !== loginModal || $[24] !== navigate || $[25] !== user) {
    t12 = user ? /*#__PURE__*/_jsxs(DropdownMenu, {
      children: [/*#__PURE__*/_jsx(DropdownMenuTrigger, {
        asChild: true,
        children: /*#__PURE__*/_jsxs(Button, {
          variant: "ghost",
          className: "gap-2 px-2",
          "data-testid": NAV_USER_MENU,
          children: [/*#__PURE__*/_jsx(Avatar, {
            className: "h-7 w-7",
            children: /*#__PURE__*/_jsx(AvatarFallback, {
              className: "bg-brand/10 text-xs text-brand",
              children: initials
            })
          }), /*#__PURE__*/_jsx("span", {
            className: "hidden text-sm md:inline",
            children: user.name
          })]
        })
      }), /*#__PURE__*/_jsxs(DropdownMenuContent, {
        align: "end",
        className: "w-48",
        children: [/*#__PURE__*/_jsxs(DropdownMenuItem, {
          onClick: () => navigate({
            to: "/mitt-konto"
          }),
          children: [/*#__PURE__*/_jsx(Settings, {
            className: "mr-2 h-4 w-4"
          }), "Mitt konto"]
        }), /*#__PURE__*/_jsx(DropdownMenuSeparator, {}), /*#__PURE__*/_jsxs(DropdownMenuItem, {
          onClick: () => {
            authClient.signOut();
            navigate({
              to: "/upptack"
            });
          },
          children: [/*#__PURE__*/_jsx(LogOut, {
            className: "mr-2 h-4 w-4"
          }), "Logga ut"]
        })]
      })]
    }) : /*#__PURE__*/_jsxs(Button, {
      variant: "ghost",
      size: "sm",
      className: "hidden gap-1.5 sm:flex",
      onClick: loginModal.onOpen,
      "data-testid": NAV_LOGIN_BUTTON,
      children: [/*#__PURE__*/_jsx(LogIn, {
        className: "h-4 w-4"
      }), "Logga in"]
    });
    $[22] = initials;
    $[23] = loginModal;
    $[24] = navigate;
    $[25] = user;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== isAdmin || $[28] !== loginModal || $[29] !== pathname || $[30] !== pendingCount || $[31] !== user) {
    t13 = /*#__PURE__*/_jsx(MobileNav, {
      pathname: pathname,
      isAdmin: isAdmin,
      pendingCount: pendingCount,
      user: user,
      loginModal: loginModal
    });
    $[27] = isAdmin;
    $[28] = loginModal;
    $[29] = pathname;
    $[30] = pendingCount;
    $[31] = user;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t11 || $[34] !== t12 || $[35] !== t13) {
    t14 = /*#__PURE__*/_jsxs("div", {
      className: "flex items-center gap-2",
      children: [t11, t12, t13]
    });
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t14 || $[38] !== t8) {
    t15 = /*#__PURE__*/_jsx("header", {
      className: "sticky top-0 z-50 w-full border-b bg-white pt-[env(safe-area-inset-top)]",
      children: /*#__PURE__*/_jsxs("div", {
        className: "flex h-14 items-center justify-between px-4",
        children: [t8, t14]
      })
    });
    $[37] = t14;
    $[38] = t8;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  return t15;
}

// ─── Mobile navigation ─────────────────────────────────────────────────────
function _temp2(n) {
  return n[0];
}
function _temp(s) {
  return s.location.pathname;
}
function MobileNav(t0) {
  const $ = _c(33);
  const {
    pathname,
    isAdmin,
    pendingCount,
    user,
    loginModal
  } = t0;
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  let t1;
  if ($[0] !== navigate) {
    t1 = to => {
      navigate({
        to
      });
      setOpen(false);
    };
    $[0] = navigate;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const go = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = /*#__PURE__*/_jsx(SheetTrigger, {
      asChild: true,
      children: /*#__PURE__*/_jsx(Button, {
        variant: "ghost",
        size: "icon",
        children: /*#__PURE__*/_jsx(Menu, {
          className: "h-5 w-5"
        })
      })
    });
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = /*#__PURE__*/_jsx("div", {
      className: "mb-4 flex items-center justify-center",
      children: /*#__PURE__*/_jsx(FFLogo, {
        variant: "full"
      })
    });
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== go || $[5] !== pathname) {
    t4 = NAV_LINKS.map(t5 => {
      const {
        to: to_0,
        label,
        icon: Icon
      } = t5;
      return /*#__PURE__*/_jsxs(Button, {
        variant: "ghost",
        className: cn("w-full justify-start gap-2 text-base font-medium", pathname.startsWith(to_0) ? "bg-brand/10 text-brand" : "text-gray-700 hover:bg-gray-100"),
        onClick: () => go(to_0),
        children: [/*#__PURE__*/_jsx(Icon, {
          className: "h-4 w-4"
        }), label]
      }, to_0);
    });
    $[4] = go;
    $[5] = pathname;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== go || $[8] !== isAdmin || $[9] !== pathname || $[10] !== pendingCount) {
    t5 = isAdmin && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("div", {
        className: "my-2 border-t"
      }), /*#__PURE__*/_jsxs(Button, {
        variant: "ghost",
        className: cn("relative w-full justify-start gap-2 text-base font-medium", pathname.startsWith("/admin") ? "bg-brand/10 text-brand" : "text-gray-700 hover:bg-gray-100"),
        onClick: () => go("/admin"),
        children: [/*#__PURE__*/_jsx(Shield, {
          className: "h-4 w-4"
        }), "Admin", pendingCount != null && Number(pendingCount) > 0 && /*#__PURE__*/_jsx("span", {
          className: "ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold text-white",
          children: Number(pendingCount)
        })]
      })]
    });
    $[7] = go;
    $[8] = isAdmin;
    $[9] = pathname;
    $[10] = pendingCount;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = /*#__PURE__*/_jsx("div", {
      className: "my-2 border-t"
    });
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== navigate) {
    t7 = () => {
      setOpen(false);
      navigate({
        to: "/bidra"
      });
    };
    $[13] = navigate;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = /*#__PURE__*/_jsx(Camera, {
      className: "h-4 w-4"
    });
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t7) {
    t9 = /*#__PURE__*/_jsxs(Button, {
      className: "w-full gap-2 bg-brand hover:bg-brand/90",
      onClick: t7,
      children: [t8, "Bidra"]
    });
    $[16] = t7;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== loginModal || $[19] !== user) {
    t10 = !user && /*#__PURE__*/_jsxs(Button, {
      variant: "outline",
      className: "w-full gap-2",
      onClick: () => {
        loginModal.onOpen();
        setOpen(false);
      },
      children: [/*#__PURE__*/_jsx(LogIn, {
        className: "h-4 w-4"
      }), "Logga in"]
    });
    $[18] = loginModal;
    $[19] = user;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== go || $[22] !== user) {
    t11 = user && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Button, {
        variant: "ghost",
        className: "w-full justify-start gap-2 text-gray-700",
        onClick: () => go("/mitt-konto"),
        children: [/*#__PURE__*/_jsx(Settings, {
          className: "h-4 w-4"
        }), "Mitt konto"]
      }), /*#__PURE__*/_jsxs(Button, {
        variant: "ghost",
        className: "w-full justify-start gap-2 text-gray-700",
        onClick: () => {
          authClient.signOut();
          go("/upptack");
        },
        children: [/*#__PURE__*/_jsx(LogOut, {
          className: "h-4 w-4"
        }), "Logga ut"]
      })]
    });
    $[21] = go;
    $[22] = user;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t4 || $[27] !== t5 || $[28] !== t9) {
    t12 = /*#__PURE__*/_jsxs(SheetContent, {
      side: "top",
      className: "w-full px-4 pb-4 pt-6",
      children: [t3, /*#__PURE__*/_jsxs("nav", {
        className: "flex flex-col gap-1",
        children: [t4, t5, t6, t9, t10, t11]
      })]
    });
    $[24] = t10;
    $[25] = t11;
    $[26] = t4;
    $[27] = t5;
    $[28] = t9;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== open || $[31] !== t12) {
    t13 = /*#__PURE__*/_jsx("div", {
      className: "md:hidden",
      children: /*#__PURE__*/_jsxs(Sheet, {
        open: open,
        onOpenChange: setOpen,
        children: [t2, t12]
      })
    });
    $[30] = open;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  return t13;
}